import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/styles';
import { Card } from 'reactstrap';
import LineChartCard from 'components/Dashboard/LineChartCard';
import PieChartCard from 'components/Dashboard/PieChartCard';
import ReportsChart from 'components/Reports/ReportsChart';
import { MIN_DESKTOP_WIDTH, MAX_LARGE_SIZE } from 'constants/misc';
import { stripDecimalsAndFormat, calculatePercentage } from 'utils/misc';
import { percentageChange } from 'utils/report';
import {
  blueberry, frenchPink, caribbeanGreen,
} from 'themes/colors';
import millify from 'utils/millify';
import dashboardChartSelector from 'selectors/dashboardChartSelector';
import DashboardListStats from './ListStats';
import DashboardFilters from './DashboardFilters';

/* eslint-disable react/prop-types */

const styles = () => ({
  firstCharts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridGap: '20px',
    marginTop: '.5rem',
    paddingBottom: '40px',
  },
  lineChart: {
    gridColumn: '1',
    height: '248px',
    padding: '24px',
    [`@media screen and (max-width: ${MAX_LARGE_SIZE}px)`]: {
      gridColumn: 'span 2',
      gridRow: 'auto',
    },
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      gridColumn: 'span 4',
      gridRow: 'auto',
    },
  },
  barChart: {
    gridColumn: '2/5',
    gridRow: '1/3',
    [`@media screen and (max-width: ${MAX_LARGE_SIZE}px)`]: {
      gridColumn: 'span 4',
      gridRow: 'auto',
    },
  },
  analytics: {
    paddingTop: '0.5rem',
  },
  pieChart: {
    padding: '24px',
    gridColumn: 'span 2',
    gridRow: 'span 1',
    [`@media screen and (max-width: ${MAX_LARGE_SIZE}px)`]: {
      gridColumn: 'span 4',
      gridRow: 'auto',
    },
  },
  listState: {
    gridColumn: 'span 4',
    gridRow: 'span 1',
    [`@media screen and (max-width: ${MAX_LARGE_SIZE}px)`]: {
      gridRow: 'auto',
    },
  },
  sampleContainer: {
    marginTop: '.5rem',
    fontSize: '.8rem',
  },
  sampleDataTag: {
    display: 'inline-block',
    backgroundColor: blueberry,
    color: 'white',
    padding: '.5rem',
    margin: '.5rem',
    textTransform: 'uppercase',
    borderRadius: '.2rem',
  },
});

const dataKeys = [
  { dataKey: 'interested', color: blueberry },
  { dataKey: 'expandedInterest', color: frenchPink },
  { dataKey: 'opened', color: caribbeanGreen },
];

const checkboxColors = {
  interested: 'blue',
  expandedInterest: 'pink',
  opened: 'green',
};

const Dashboard = (props) => {
  const {
    classes, location, match,
    graphData, chartLoading, dashboardStats,
  } = props;

  const potentialReachTotal = String(millify(parseInt(dashboardStats.potential_reach_total, 10))).toUpperCase();

  return (
    <div>
      <DashboardFilters />
      {dashboardStats.isMocked &&
        <div>
          <Card className={classes.sampleContainer}>
            <div>
              <span className={classes.sampleDataTag}> Sample data </span>
              Your campaign performance data will appear here after launching your first campaign.
            </div>
          </Card>
        </div>}
      <div className={classes.firstCharts}>
        <LineChartCard
          className={classes.lineChart}
          title="Potential Reach"
          value={potentialReachTotal}
          tooltip={stripDecimalsAndFormat(dashboardStats.potential_reach_total)}
          growth={percentageChange(
            dashboardStats.last_period_potential_reach_total,
            dashboardStats.potential_reach_total,
          )}
          graphData={dashboardStats.potential_reach_charts}
          dataKey="potential_reach"
          chartColor="blue"
          loading={chartLoading}
          dataIntercomTarget="Potential Reach Box"
        />
        <LineChartCard
          className={classes.lineChart}
          title="Interested Rate"
          value={`${calculatePercentage(dashboardStats.people_contacted, dashboardStats.interested)}%`}
          growth={percentageChange(dashboardStats.last_period_interested, dashboardStats.interested)}
          graphData={graphData}
          dataKey="interested"
          chartColor="yellow"
          loading={chartLoading}
        />
        <Card className={classes.barChart}>
          <ReportsChart
            className={classes.analytics}
            loading={chartLoading}
            dataKeys={dataKeys}
            dataSource={graphData}
            checkboxColors={checkboxColors}
          />
        </Card>
        <PieChartCard
          className={classes.pieChart}
          loading={chartLoading}
          dataSource={dashboardStats.interested_by_subject || []}
          title="Interested by contact subject"
        />
        <PieChartCard
          className={classes.pieChart}
          loading={chartLoading}
          dataSource={dashboardStats.interested_by_outlet_type || []}
          title="Interested by outlet type"
        />
        <DashboardListStats className={classes.listState} location={location} match={match} />
      </div>
    </div>
  );
};


Dashboard.propTypes = {
  classes: PropTypes.element.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  graphData: PropTypes.shape({}),
  report: PropTypes.shape({
    loading: PropTypes.bool,
  }),
  graphStats: PropTypes.shape({}),
  chartLoading: PropTypes.bool,
  potentialReach: PropTypes.shape({}),
  dashboardStats: PropTypes.shape({}),
};

Dashboard.defaultProps = {
  graphData: null,
  report: null,
  graphStats: null,
  chartLoading: false,
  potentialReach: null,
  dashboardStats: null,
};

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(state => ({
    graphData: dashboardChartSelector(state.dashboard),
    chartLoading: state.dashboard.loading,
    dashboardStats: state.dashboard,
  })),
)(Dashboard);
